// @ts-nocheck
import React, { useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { formatNumber, cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DatePeriodFilter,
  useDatePeriodFilter,
} from "@/components/shared/DatePeriodFilter";
import { ListPageShell } from "@/components/shared/ListPageShell";
import { useHeaderSearch } from "@/hooks/useHeaderSearch";
import { SegmentedViewSwitcher, type ViewOption } from "@/components/shared/SegmentedViewSwitcher";
import { ProductionLogForm } from "./components/ProductionLogForm";
import { ShopfloorScannerModal } from "@/components/factory/ShopfloorScannerModal";
import MachineLogsPage from "../machine-logs/MachineLogList";
import { useConfirm } from "@/providers/ConfirmProvider";
import { toast } from "sonner";
import {
  ClipboardList,
  Gauge,
  Plus,
  Trash2,
  Download,
  RefreshCw,
  Search,
  CheckCircle2,
  AlertCircle,
  Clock,
  Layers,
  Percent,
  Calendar,
  ChevronDown,
  ChevronRight,
  ScanBarcode,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  DateGroupedTable,
  DateGroupToggleButton,
  extractDateKey,
} from "@/components/shared/DateGroupedTable";
import { useMachineLookup } from "@/hooks/useFKLookup";
import { PROCESS_STAGES } from "@/pages/planning/process-planning/ProcessPlanningList";
import { useRowSelection } from "@/hooks/useRowSelection";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import { BulkActionBar } from "@/components/shared/BulkActionBar";
import { ProductionThumbnailCell } from "@/components/shared/ProductionThumbnailCell";
import { exportToExcel, formatDateForFile } from "@/lib/excel.utils";
import { usePageSizeState } from "@/hooks/usePreferredPageSize";

type FactoryLogViewMode = "shift" | "machine";

const LOG_VIEW_OPTIONS: ViewOption<FactoryLogViewMode>[] = [
  {
    id: "shift",
    label: "Nhật Ký Ca",
    icon: ClipboardList,
  },
  {
    id: "machine",
    label: "Thiết Bị & OEE",
    icon: Gauge,
  },
];

export function FactoryLogList() {
  const stripTags = (str: string) => str.replace(/<[^>]*>/g, '').trim();
  const navigate = useNavigate();
  const searchParams = useSearch({ strict: false }) as {
    tab?: string;
    view?: string;
  };
  const currentView: FactoryLogViewMode = searchParams?.view === "machine" ? "machine" : "shift";

  const queryClient = useQueryClient();
  const confirm = useConfirm();

  // Unified Top Header Search (Rule 3)
  const { searchTerm: headerSearch } = useHeaderSearch();

  // Filters & State
  const [machineFilter, setMachineFilter] = useState<string>("ALL");
  const [processTypeFilter, setProcessTypeFilter] = useState<string>("ALL");
  const [shiftFilter, setShiftFilter] = useState<string>("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = usePageSizeState(25);
  const [isDateGrouped, setIsDateGrouped] = useState(true);

  // Form & Scanner State
  const [formOpen, setFormOpen] = useState(false);
  const [editingLog, setEditingLog] = useState<any>(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [selectedPrefill, setSelectedPrefill] = useState<any>(undefined);

  const handleOpenDetailedLogFromScanner = (prefill: any) => {
    setScannerOpen(false);
    setEditingLog(null);
    setSelectedPrefill(prefill);
    setFormOpen(true);
  };

  const {
    period,
    setPeriod,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    dateParams,
  } = useDatePeriodFilter();

  const { options: machineOptions } = useMachineLookup();

  // Reset page on search change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [headerSearch]);

  // Query Production Logs
  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      "production-logs",
      currentPage,
      pageSize,
      headerSearch,
      machineFilter,
      processTypeFilter,
      shiftFilter,
      dateParams.from,
      dateParams.to,
    ],
    queryFn: () =>
      trpcClient.productionLogs.list.query({
        page: currentPage,
        limit: pageSize,
        search: headerSearch || undefined,
        machineId: machineFilter !== "ALL" ? machineFilter : undefined,
        processType: processTypeFilter !== "ALL" ? processTypeFilter : undefined,
        dateFrom: dateParams.from,
        dateTo: dateParams.to,
      }),
    enabled: currentView === "shift",
  });

  const items = data?.items || [];
  const meta = data?.meta || { total: 0, page: 1, limit: pageSize, totalPages: 0 };
  const summary = data?.summary || {
    totalLogs: 0,
    totalQtyOk: 0,
    totalQtyNg: 0,
    avgQualityPercent: 100,
  };

  const { hasPermission } = useRoleAccess();
  const canDelete = hasPermission("production-logs", "delete");

  // Chọn nhiều dòng
  const rowIds = React.useMemo(() => items.map((i: any) => i.id), [items]);
  const rowSelection = useRowSelection(rowIds);
  const selectedItems = React.useMemo(
    () => items.filter((i: any) => rowSelection.selectedIds.has(i.id)),
    [items, rowSelection.selectedIds]
  );

  const openLog = (item: any) => {
    setEditingLog(item);
    setSelectedPrefill(undefined);
    setFormOpen(true);
  };

  const exportRows = async (rows: any[], suffix = "") => {
    await exportToExcel(
      rows,
      [
        { key: "logId", label: "Mã log", width: 16, format: (_v, r: any) => r.logId || r.id?.substring(0, 8) },
        { key: "date", label: "Ngày", width: 12, format: (v) => (v ? new Date(v).toLocaleDateString("vi-VN") : "") },
        { key: "shift", label: "Ca", width: 8 },
        { key: "poCode", label: "Lệnh SX", width: 16, format: (_v, r: any) => r.productionOrder?.orderId || r.productionOrderId || "" },
        { key: "customer", label: "Khách hàng", width: 22, format: (_v, r: any) => r.productionOrder?.customer?.shortName || r.productionOrder?.customer?.name || "" },
        { key: "product", label: "Sản phẩm", width: 32, format: (_v, r: any) => r.productionOrder?.product?.productName || r.productionOrder?.productName || r.productionOrder?.orderDetail?.product?.productName || "" },
        { key: "step", label: "Công đoạn", width: 20, format: (_v, r: any) => r.routingStep?.name || r.routingStep?.process?.name || "" },
        { key: "machine", label: "Máy", width: 18, format: (_v, r: any) => r.machine?.name || "" },
        { key: "operator", label: "Thợ vận hành", width: 18, format: (_v, r: any) => r.operator?.name || r.operator?.username || "" },
        { key: "qtyOk", label: "SL đạt", width: 10, type: "number" },
        { key: "qtyNg", label: "SL hỏng", width: 10, type: "number" },
        { key: "notes", label: "Ghi chú", width: 30, format: (v) => (v ? stripTags(String(v)) : "") },
      ],
      `Nhat-ky-san-xuat${suffix}_${formatDateForFile()}`,
      { sheetName: "Nhật ký sản xuất" }
    );
  };

  const handleExport = async () => {
    try {
      const all = await trpcClient.productionLogs.list.query({
        page: 1,
        limit: 5000,
        search: headerSearch || undefined,
        machineId: machineFilter !== "ALL" ? machineFilter : undefined,
        processType: processTypeFilter !== "ALL" ? processTypeFilter : undefined,
        dateFrom: dateParams.from,
        dateTo: dateParams.to,
      });
      await exportRows(all?.items || []);
    } catch (e: any) {
      toast.error(e?.message || "Không xuất được Excel");
    }
  };

  const handleBulkDelete = async () => {
    const ids = [...rowSelection.selectedIds];
    if (ids.length === 0) return;
    const ok = await confirm({
      title: "Xóa nhật ký sản xuất",
      message: `Xóa ${ids.length} nhật ký đã chọn? Số lượng của công đoạn và Lệnh sản xuất liên quan sẽ được hoàn tác.`,
      confirmText: "Xóa",
    });
    if (!ok) return;
    let done = 0;
    for (const id of ids) {
      try {
        await trpcClient.productionLogs.delete.mutate({ id });
        done++;
      } catch (err: any) {
        toast.error(err?.message || "Xóa thất bại");
      }
    }
    if (done > 0) toast.success(`Đã xóa ${done}/${ids.length} nhật ký`);
    rowSelection.clearSelection();
    refetch();
    queryClient.invalidateQueries({ queryKey: ["stage-work-orders"] });
    queryClient.invalidateQueries({ queryKey: ["production-logs"] });
    queryClient.invalidateQueries({ queryKey: ["production-orders"] });
  };

  const handleViewChange = (view: FactoryLogViewMode) => {
    navigate({
      search: ((prev: any) => ({ ...prev, view: view === "shift" ? undefined : view })) as any,
    });
  };

  const handleDelete = async (id: string) => {
    const isConfirmed = await confirm({
      title: "Xóa nhật ký sản xuất",
      message:
        "Bạn có chắc muốn xóa nhật ký này? Thao tác này sẽ tự động khấu trừ ngược số lượng của công đoạn và Lệnh sản xuất liên quan.",
      confirmText: "Xóa",
    });

    if (isConfirmed) {
      try {
        await trpcClient.productionLogs.delete.mutate({ id });
        toast.success("Xóa nhật ký thành công! Số lượng công đoạn đã được hoàn tác.");
        refetch();
        queryClient.invalidateQueries({ queryKey: ["stage-work-orders"] });
        queryClient.invalidateQueries({ queryKey: ["production-logs"] });
        queryClient.invalidateQueries({ queryKey: ["production-orders"] });
      } catch (err: any) {
        toast.error(err.message || "Xóa thất bại");
      }
    }
  };

  if (currentView === "machine") {
    return (
      <MachineLogsPage
        viewSwitcher={
          <SegmentedViewSwitcher
            options={LOG_VIEW_OPTIONS}
            value={currentView}
            onChange={handleViewChange}
            size="sm"
          />
        }
      />
    );
  }

  return (
    <>
      <ListPageShell
        trashModule="production-logs"
        unitLabel="nhật ký"
        totalItems={meta.total || 0}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setCurrentPage(1);
        }}
        isLoading={isLoading}
        tabs={
          <SegmentedViewSwitcher
            options={LOG_VIEW_OPTIONS}
            value={currentView}
            onChange={handleViewChange}
            size="sm"
          />
        }
        actions={
          <div className="flex items-center gap-1.5 shrink-0 flex-nowrap">
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs font-semibold border-indigo-200 text-indigo-700 hover:bg-indigo-50 shadow-2xs flex items-center gap-1.5 cursor-pointer shrink-0"
              onClick={() => setScannerOpen(true)}
              title="Quét barcode hoặc nhập mã LSX để xem công đoạn và ghi nhật ký"
            >
              <ScanBarcode className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
              <span>Quét LSX</span>
            </Button>

            <Button
              size="sm"
              className="h-7 text-xs font-semibold bg-teal-600 hover:bg-teal-700 text-white shadow-2xs flex items-center gap-1.5 cursor-pointer shrink-0"
              onClick={() => {
                setEditingLog(null);
                setSelectedPrefill(undefined);
                setFormOpen(true);
              }}
            >
              <Plus className="w-3.5 h-3.5 shrink-0" />
              <span>Thêm Nhật Ký</span>
            </Button>
          </div>
        }
        onExport={handleExport}
        dateFilter={
          <div className="flex items-center gap-1.5 shrink-0 flex-nowrap [&_button]:!h-7">
            <DatePeriodFilter
              period={period}
              setPeriod={setPeriod}
              customFrom={customFrom}
              setCustomFrom={setCustomFrom}
              customTo={customTo}
              setCustomTo={setCustomTo}
            />
            <DateGroupToggleButton
              isGrouped={isDateGrouped}
              onToggle={() => setIsDateGrouped(!isDateGrouped)}
              className="h-7 px-2 text-xs"
            />
          </div>
        }
        headerFilters={
            <div className="flex items-center gap-2 shrink-0 flex-nowrap">
              <Select value={machineFilter} onValueChange={(val) => { setMachineFilter(val); setCurrentPage(1); }}>
                <SelectTrigger className="!h-7 text-xs w-[155px] bg-white border-slate-200 shrink-0">
                  <SelectValue placeholder="Chọn máy..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL" className="text-xs">Tất cả máy móc</SelectItem>
                  {machineOptions.map((m) => (
                    <SelectItem key={m.id} value={m.id} className="text-xs">
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={processTypeFilter} onValueChange={(val) => { setProcessTypeFilter(val); setCurrentPage(1); }}>
                <SelectTrigger className="!h-7 text-xs w-[155px] bg-white border-slate-200 shrink-0">
                  <SelectValue placeholder="Công đoạn" />
                </SelectTrigger>
                <SelectContent>
                  {PROCESS_STAGES.map((stage) => (
                    <SelectItem key={stage.key} value={stage.key} className="text-xs">
                      {stage.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
        }
        topBanner={
              <div className="erp-logs-filter-row flex items-center gap-1 overflow-x-auto hide-scrollbar flex-nowrap">
                <span className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-emerald-50/90 border border-emerald-200/80 text-emerald-700 text-[11px] font-medium shrink-0">
                  <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
                  <span>SL Đạt (OK):</span>
                  <strong className="font-mono font-bold text-emerald-900">
                    {formatNumber(summary.totalQtyOk)}
                  </strong>
                </span>

                <span className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-rose-50/90 border border-rose-200/80 text-rose-700 text-[11px] font-medium shrink-0">
                  <AlertCircle className="w-3 h-3 text-rose-600 shrink-0" />
                  <span>Phế phẩm (NG):</span>
                  <strong className="font-mono font-bold text-rose-900">
                    {formatNumber(summary.totalQtyNg)}
                  </strong>
                </span>

                <span className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-indigo-50/90 border border-indigo-200/80 text-indigo-700 text-[11px] font-semibold shrink-0">
                  <Percent className="w-3 h-3 text-indigo-600 shrink-0" />
                  <span>Tỷ lệ Đạt:</span>
                  <strong className="font-mono font-bold text-indigo-900">
                    {summary.avgQualityPercent}%
                  </strong>
                </span>
              </div>
        }
      >
        <table className="w-full text-xs border-collapse min-w-[1100px]">
              <thead className="sticky top-0 z-30 bg-slate-100 border-b border-slate-200 text-slate-700 select-none">
                <tr className="whitespace-nowrap">
                  {/* Sticky Left: Log ID & Date */}
                  <th
                    className="px-3 py-2 text-left font-bold w-[130px] sticky left-0 z-40 border-r border-slate-200 shadow-[2px_0_4px_-1px_rgba(0,0,0,0.06)] whitespace-nowrap"
                    style={{ backgroundColor: "#f1f5f9", isolation: "isolate" }}
                  >
                    <div className="flex items-center gap-2">
                      <DataTableSelectAllCheckbox rowSelection={rowSelection} asChild />
                      <span>Mã Log</span>
                    </div>
                  </th>
                  <th className="px-3 py-2 text-left font-bold w-[150px] whitespace-nowrap">Lệnh SX</th>
                  <th className="px-3 py-2 text-left font-bold min-w-[180px] whitespace-nowrap">Sản Phẩm</th>
                  <th className="px-3 py-2 text-left font-bold w-[130px] whitespace-nowrap">Công Đoạn</th>
                  <th className="px-3 py-2 text-left font-bold w-[120px] whitespace-nowrap">Máy</th>
                  <th className="px-3 py-2 text-left font-bold w-[130px] whitespace-nowrap">Thợ Vận Hành</th>
                  <th className="px-3 py-2 text-left font-bold w-[120px] whitespace-nowrap">Thời Gian</th>
                  <th className="px-3 py-2 text-left font-bold w-[130px] whitespace-nowrap">SL Đạt / Hỏng</th>
                  <th className="px-3 py-2 text-left font-bold w-[140px] whitespace-nowrap">Ghi Chú</th>
                  {/* Sticky Right: Actions */}
                  <th
                    className="px-3 py-2 text-right font-bold w-[80px] sticky right-0 z-40 border-l border-slate-200 shadow-[-2px_0_4px_-1px_rgba(0,0,0,0.06)] whitespace-nowrap"
                    style={{ backgroundColor: "#f1f5f9", isolation: "isolate" }}
                  >
                    Thao Tác
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {isLoading ? (
                  <tr>
                    <td colSpan={10} className="h-32 text-center text-slate-500">
                      <div className="flex items-center justify-center gap-2">
                        <RefreshCw className="w-4 h-4 animate-spin text-teal-600" />
                        <span>Đang tải nhật ký sản xuất...</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <DateGroupedTable<any>
                    items={items}
                    isGrouped={isDateGrouped}
                    getDateKey={(item) => extractDateKey(item.date || item.createdAt)}
                    emptyState={
                      <tr>
                        <td colSpan={10} className="h-32 text-center text-slate-500">
                          <div className="flex flex-col items-center justify-center gap-1.5">
                            <ClipboardList className="w-6 h-6 text-slate-300" />
                            <span className="font-medium">Chưa có bản ghi nhật ký sản xuất</span>
                            <span className="text-[11px] text-muted-foreground">
                              Bấm nút "+ Thêm Nhật Ký" hoặc sang trang Kế Hoạch Công Đoạn để ghi nhận
                            </span>
                          </div>
                        </td>
                      </tr>
                    }
                    renderGroupHeader={(group) => {
                      const groupQtyOk = group.items.reduce((s, it) => s + (Number(it.qtyOk) || 0), 0);
                      const groupQtyNg = group.items.reduce((s, it) => s + (Number(it.qtyNg) || 0), 0);
                      const groupTotalQty = groupQtyOk + groupQtyNg;
                      const groupQualityPct = groupTotalQty > 0 ? Math.round((groupQtyOk / groupTotalQty) * 100) : 100;

                      return (
                        <tr
                          key={`group-${group.dateKey}`}
                          className={cn(
                            "border-y font-semibold text-xs transition-colors select-none",
                            group.isToday
                              ? "bg-[#eef2ff] hover:bg-[#e0e7ff] border-indigo-200"
                              : "bg-[#f1f5f9] hover:bg-[#e2e8f0] border-slate-300",
                          )}
                        >
                          {/* Sticky Left: Log ID & Date */}
                          <td
                            className="px-3 py-2 sticky left-0 z-20 border-r border-slate-200 shadow-[2px_0_4px_-1px_rgba(0,0,0,0.06)]"
                            style={{ backgroundColor: group.isToday ? "#eef2ff" : "#f1f5f9", isolation: "isolate" }}
                          >
                            <div className="flex items-center gap-1.5">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 p-0 text-slate-600 hover:text-slate-900 shrink-0"
                                onClick={group.toggleCollapse}
                                title={group.isCollapsed ? "Bung ra" : "Thu gọn"}
                              >
                                {group.isCollapsed ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                              </Button>
                              <span className="font-bold text-slate-800 text-[12px] whitespace-nowrap">
                                {group.formattedDate}
                              </span>
                            </div>
                          </td>

                          {/* Columns 2-7: Info */}
                          <td colSpan={6} className="px-3 py-2 text-slate-700">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="bg-white/80 text-slate-700 border-slate-300 text-[11px] font-bold px-2 py-0">
                                {group.count} lượt ca máy
                              </Badge>
                              {group.isToday && (
                                <Badge className="bg-indigo-600 text-white text-[10px] font-bold px-1.5 py-0 border-0">
                                  HÔM NAY
                                </Badge>
                              )}
                              {group.isCollapsed && (
                                <span className="text-[11px] text-slate-500 font-normal italic">
                                  (Đang thu gọn - bấm để xem chi tiết)
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Column 8: Sản Lượng Đạt / Hỏng */}
                          <td className="px-3 py-2 text-left font-mono font-bold text-slate-800 text-[11px] whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <span className="text-emerald-700">{formatNumber(groupQtyOk)} đạt</span>
                              {groupQtyNg > 0 && <span className="text-rose-600">({formatNumber(groupQtyNg)} hỏng)</span>}
                            <span className="text-[10px] text-slate-500 font-normal">· Tỷ lệ: <strong className={groupQualityPct >= 95 ? "text-emerald-600" : "text-amber-600"}>{groupQualityPct}%</strong></span>
                            </div>
                          </td>

                          {/* Column 9: Defect / Notes */}
                          <td className="px-3 py-2 text-slate-400 font-mono text-[10px]">—</td>

                          {/* Column 10: Sticky Right Thao Tác */}
                          <td
                            className="px-3 py-2 sticky right-0 z-20 border-l border-slate-200 shadow-[-2px_0_4px_-1px_rgba(0,0,0,0.06)]"
                            style={{ backgroundColor: group.isToday ? "#eef2ff" : "#f1f5f9", isolation: "isolate" }}
                          ></td>
                        </tr>
                      );
                    }}
                    renderRow={(item, idx) => {
                    const isEven = idx % 2 === 0;
                    const solidRowBg = isEven ? "#ffffff" : "#f8fafc";
                    const poCode = item.productionOrder?.orderId || item.productionOrderId;
                    const customerName = item.productionOrder?.customer?.shortName || item.productionOrder?.customer?.name;
                    const productName = item.productionOrder?.product?.productName || item.productionOrder?.productName || item.productionOrder?.orderDetail?.product?.productName;
                    const productCode = item.productionOrder?.product?.productCode || item.productionOrder?.orderDetail?.product?.productCode;
                    const stepName = item.routingStep?.name || item.routingStep?.process?.name || "Công đoạn";
                    const stepOrder = item.routingStep?.stepOrder;
                    const totalQty = (item.qtyOk || 0) + (item.qtyNg || 0);
                    const qualityPct = totalQty > 0 ? Math.round(((item.qtyOk || 0) / totalQty) * 100) : 100;

                    return (
                      <DataTableRow
                        key={item.id}
                        id={item.id}
                        rowSelection={rowSelection}
                        onClick={() => openLog(item)}
                        className="hover:bg-teal-50/40 transition-colors group whitespace-nowrap"
                      >
                        {/* Sticky Left: Log ID & Date */}
                        <td
                          className="px-3 py-2 align-top sticky left-0 z-20 border-r border-slate-200 shadow-[2px_0_4px_-1px_rgba(0,0,0,0.06)]"
                          style={{ backgroundColor: solidRowBg, isolation: "isolate" }}
                        >
                          <div className="flex items-start gap-2">
                          <span className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                            <DataTableRowCheckbox id={item.id} rowSelection={rowSelection} asChild />
                          </span>
                          <div className="flex flex-col gap-0.5">
                            <span className="font-mono font-bold text-slate-900">
                              {item.logId || item.id.substring(0, 8)}
                            </span>
                            <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                              <span>{new Date(item.date).toLocaleDateString("vi-VN")}</span>
                              {item.shift && (
                                <span className="px-1 py-0.2 rounded bg-slate-100 font-semibold text-slate-700">
                                  {item.shift}
                                </span>
                              )}
                            </div>
                          </div>
                          </div>
                        </td>

                        {/* PO & Customer */}
                        <td className="px-3 py-2 align-top">
                          <div className="flex flex-col gap-0.5">
                            <span className="font-mono font-bold text-teal-700 text-left">
                              {poCode || "—"}
                            </span>
                            <span className="text-[11px] text-muted-foreground min-w-[150px] break-words whitespace-normal leading-tight">
                              {customerName || "Khách lẻ"}
                            </span>
                          </div>
                        </td>

                        {/* Product */}
                        <td className="px-3 py-2 align-top">
                          <div className="flex items-start gap-2">
                            <ProductionThumbnailCell
                              imageUrl={item.productionOrder?.product?.image || item.productionOrder?.orderDetail?.product?.image}
                              layoutUrl={item.productionOrder?.printLayouts?.[0]?.fileUrl}
                              title={productName}
                              subtitle={productCode}
                              size={34}
                            />
                            <div className="flex flex-col gap-0.5 min-w-0">
                              <span className="font-semibold text-slate-800 line-clamp-1" title={productName}>
                                {productName || "—"}
                              </span>
                              {productCode && (
                                <span className="font-mono text-[10px] text-muted-foreground">
                                  {productCode}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Routing Step (FK) */}
                        <td className="px-3 py-2 align-top">
                          <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-1.5">
                              {stepOrder && (
                                <span className="px-1.5 py-0.2 rounded text-[10px] font-mono font-bold bg-slate-200 text-slate-800">
                                  B{stepOrder}
                                </span>
                              )}
                              <span className="font-semibold text-slate-900">
                                {stepName}
                              </span>
                            </div>
                            {item.routingStep?.process?.type && (
                              <span className="text-[10px] text-muted-foreground">
                                {item.routingStep.process.type}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Machine (FK) */}
                        <td className="px-3 py-2 align-top">
                          <span className="font-medium text-slate-800">
                            {item.machine?.name || (item.machineId ? `Máy ${item.machineId}` : "—")}
                          </span>
                        </td>

                        {/* Operator (FK) */}
                        <td className="px-3 py-2 align-top">
                          <div className="flex flex-col gap-0.5">
                            <span className="font-medium text-slate-800">
                              {item.operator?.name || item.operator?.username || "—"}
                            </span>
                            {item.operatorTeam && (
                              <span className="text-[10px] text-muted-foreground">
                                Tổ: {item.operatorTeam}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Run Times */}
                        <td className="px-3 py-2 align-top">
                          {item.runStartTime ? (
                            <div className="flex flex-col gap-0.5 text-[11px] font-mono">
                              <span className="text-slate-700">
                                {new Date(item.runStartTime).toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" })}
                                {item.runEndTime && ` → ${new Date(item.runEndTime).toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" })}`}
                              </span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>

                        {/* Output OK & NG */}
                        <td className="px-3 py-2 align-top">
                          <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-2 font-mono">
                              <span className="font-bold text-emerald-600">
                                {formatNumber(item.qtyOk)} đạt
                              </span>
                              {item.qtyNg > 0 && (
                                <span className="font-semibold text-rose-600">
                                  ({formatNumber(item.qtyNg)} hỏng)
                                </span>
                              )}
                            </div>
                            <span className="text-[10px] text-muted-foreground">
                              Tỷ lệ đạt: <strong className={qualityPct >= 95 ? "text-emerald-600" : "text-amber-600"}>{qualityPct}%</strong>
                            </span>
                          </div>
                        </td>

                        {/* Defect Reason & Notes */}
                        <td className="px-3 py-2 align-top">
                          <div className="flex flex-col gap-0.5">
                            {item.defectReason && (
                              <span className="text-[11px] font-semibold text-rose-700 line-clamp-1" title={stripTags(item.defectReason)}>
                                Lỗi: {stripTags(item.defectReason)}
                              </span>
                            )}
                            {item.notes && (
                              <span className="text-[11px] text-muted-foreground line-clamp-1" title={stripTags(item.notes)}>
                                {stripTags(item.notes)}
                              </span>
                            )}
                            {!item.defectReason && !item.notes && <span className="text-muted-foreground">—</span>}
                          </div>
                        </td>

                        {/* Sticky Right: Actions */}
                        <td
                          className="px-3 py-2 align-middle text-right sticky right-0 z-20 border-l border-slate-200 shadow-[-2px_0_4px_-1px_rgba(0,0,0,0.06)]"
                          style={{ backgroundColor: solidRowBg, isolation: "isolate" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-slate-500 hover:text-rose-600"
                              onClick={() => handleDelete(item.id)}
                              title="Xóa nhật ký (hoàn tác số lượng)"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </DataTableRow>
                    );
                  }}
                  renderPageSummary={(pageItems) => {
                    const pageQtyOk = pageItems.reduce((s, it) => s + (Number(it.qtyOk) || 0), 0);
                    const pageQtyNg = pageItems.reduce((s, it) => s + (Number(it.qtyNg) || 0), 0);
                    const pageTotal = pageQtyOk + pageQtyNg;
                    const pagePct = pageTotal > 0 ? Math.round((pageQtyOk / pageTotal) * 100) : 100;

                    return (
                      <tr
                        key="page-summary-row"
                        className="border-t-2 border-slate-300 bg-[#f8fafc] font-bold text-xs"
                      >
                        <td
                          className="px-3 py-2.5 sticky left-0 z-20 border-r border-slate-200 shadow-[2px_0_4px_-1px_rgba(0,0,0,0.06)] bg-[#f8fafc]"
                          style={{ isolation: "isolate" }}
                        >
                          <span className="font-bold text-slate-900 text-xs">
                            Tổng trang:
                          </span>
                        </td>
                        <td colSpan={6} className="px-3 py-2.5 text-slate-800">
                          <Badge
                            variant="secondary"
                            className="bg-slate-200 text-slate-800 text-[11px] font-bold px-2 py-0"
                          >
                            {pageItems.length} lượt chạy ca
                          </Badge>
                        </td>
                        <td className="px-3 py-2.5 text-left font-mono font-bold text-[11px] whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <span className="text-emerald-700">{formatNumber(pageQtyOk)} đạt</span>
                            {pageQtyNg > 0 && <span className="text-rose-600">({formatNumber(pageQtyNg)} hỏng)</span>}
                            <span className="text-[10px] text-slate-600 font-normal">
                              · Tỷ lệ TB: <strong className={pagePct >= 95 ? "text-emerald-600" : "text-amber-600"}>{pagePct}%</strong>
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-slate-400 font-mono text-[10px]">—</td>
                        <td
                          className="px-3 py-2.5 sticky right-0 z-20 border-l border-slate-200 shadow-[-2px_0_4px_-1px_rgba(0,0,0,0.06)] bg-[#f8fafc]"
                          style={{ isolation: "isolate" }}
                        ></td>
                      </tr>
                    );
                  }}
                />
              )}
              </tbody>
            </table>
      </ListPageShell>

      <BulkActionBar
        selectedCount={rowSelection.selectedCount}
        onClearSelection={rowSelection.clearSelection}
        actions={[
          {
            label: "Xuất Excel",
            icon: <Download className="w-3.5 h-3.5" />,
            onClick: async () => {
              try {
                await exportRows(selectedItems, "_chon");
              } catch (e: any) {
                toast.error(e?.message || "Không xuất được Excel");
              }
            },
          },
          ...(canDelete
            ? [
                {
                  label: "Xóa",
                  icon: <Trash2 className="w-3.5 h-3.5" />,
                  variant: "destructive" as const,
                  onClick: handleBulkDelete,
                },
              ]
            : []),
        ]}
      />

      {/* Shopfloor Barcode Scanner Modal */}
      {scannerOpen && (
        <ShopfloorScannerModal
          open={scannerOpen}
          onOpenChange={setScannerOpen}
          onOpenLogForm={handleOpenDetailedLogFromScanner}
        />
      )}

      {/* Canonical Production Log Form Modal (Rule 2.1) */}
      {formOpen && (
        <ProductionLogForm
          open={formOpen}
          onOpenChange={(v) => {
            setFormOpen(v);
            if (!v) setSelectedPrefill(undefined);
          }}
          initialData={editingLog}
          prefill={selectedPrefill}
          onSuccess={() => {
            refetch();
            queryClient.invalidateQueries({ queryKey: ["production-logs"] });
            queryClient.invalidateQueries({ queryKey: ["stage-work-orders"] });
          }}
        />
      )}
    </>
  );
}

export default FactoryLogList;
